import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useMeCore } from "../../../cores/me";
import { useEmailMarketingCore } from "../../../cores/emailMarketing";
import { useRouterCore } from "../../../cores/router";
import Content from "../../layout/Content";
import Layout from "../../layout/Layout";
import { EmailComposer } from "./EmailComposer";
import {
  EmailTemplateLibrary,
  emailTemplateLibraryFilters,
} from "./EmailTemplateLibrary";
import { DEFAULT_RICH_HTML, wrapRichHtmlForEmail } from "./emailHtmlUtils";
import "./EmailMarketing.scss";

export const EmailMarketingTemplatesScreen = () => {
  const intl = useIntl();
  const { company } = useMeCore();
  const { goToEmailMarketingEmailsFromTemplate } = useRouterCore();
  const {
    templates: apiTemplates,
    loadTemplates,
    createTemplate,
  } = useEmailMarketingCore();
  const [filter, setFilter] = useState<"all" | "dragDrop" | "html" | "plain">("all");
  const [composerOpen, setComposerOpen] = useState(false);
  const [templateSubject, setTemplateSubject] = useState(
    intl.formatMessage({ id: "app.emailMarketing.templates.defaultName" })
  );
  const [templateHtml, setTemplateHtml] = useState(DEFAULT_RICH_HTML);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (company?.id) {
      loadTemplates(company.id);
    }
  }, [company?.id, loadTemplates]);

  const libraryTemplates = useMemo(
    () =>
      (filter === "all"
        ? apiTemplates
        : apiTemplates.filter((template) => template.type === filter)
      ).map((template) => ({
        id: template.id,
        name: template.name,
        updatedAt: template.updatedAt,
        accent: template.accent,
        filterType: template.type,
      })),
    [apiTemplates, filter]
  );

  const openNewTemplate = () => {
    setTemplateSubject(
      intl.formatMessage({ id: "app.emailMarketing.templates.defaultName" })
    );
    setTemplateHtml(DEFAULT_RICH_HTML);
    setComposerOpen(true);
  };

  const saveTemplate = async () => {
    if (!company?.id) return;
    setSaving(true);
    const saved = await createTemplate(company.id, {
      name: templateSubject,
      subject: templateSubject,
      html: wrapRichHtmlForEmail(templateHtml),
      editor: "code",
    });
    setSaving(false);
    if (saved) {
      setComposerOpen(false);
      await loadTemplates(company.id);
    }
  };

  return (
    <Layout>
      <Content className="email-mkt email-mkt--templates">
        <EmailTemplateLibrary
          eyebrow={<FormattedMessage id="app.sidebar.emailMarketing" />}
          title={<FormattedMessage id="app.emailMarketing.templates.title" />}
          subtitle={<FormattedMessage id="app.emailMarketing.templates.subtitle" />}
          statsLabel={<FormattedMessage id="app.emailMarketing.templates.stats.total" />}
          statsCount={apiTemplates.length}
          templates={libraryTemplates}
          filters={emailTemplateLibraryFilters.marketing}
          activeFilter={filter}
          onFilterChange={(next) =>
            setFilter(next as "all" | "dragDrop" | "html" | "plain")
          }
          createLabel={<FormattedMessage id="app.emailMarketing.templates.create" />}
          onCreate={openNewTemplate}
          actionLabel={<FormattedMessage id="app.emailMarketing.templates.use" />}
          onTemplateAction={goToEmailMarketingEmailsFromTemplate}
          emptyLabel={<FormattedMessage id="app.emailMarketing.templates.empty" />}
        />
        <EmailComposer
          open={composerOpen}
          subject={templateSubject}
          html={templateHtml}
          initialMode="rich"
          saving={saving}
          onSubjectChange={setTemplateSubject}
          onHtmlChange={setTemplateHtml}
          onClose={() => setComposerOpen(false)}
          onSaveDraft={saveTemplate}
          saveLabelId="app.emailMarketing.templates.save"
        />
      </Content>
    </Layout>
  );
};
